// ─────────────────────────────────────────────────────────────────────────────
// consent.storage.js
//
// GDPR / ePrivacy compliance:
//  - Súhlas je uložený v localStorage s verziou politiky.
//  - Ak sa verzia zmení, starý súhlas sa ignoruje a používateľ musí znova
//    rozhodnúť. Tým sa zabezpečí nová informovanosť pri zmene podmienok.
//  - Predvolený stav: iba nevyhnutné cookies (opt-in model).
// ─────────────────────────────────────────────────────────────────────────────

export const STORAGE_KEY = "cookie_consent";

// Zvýšte verziu pri každej materiálnej zmene cookie politiky.
// Starý uložený súhlas sa automaticky zneplatní.
export const POLICY_VERSION = "1.1";

/**
 * Predvolený súhlas – iba nevyhnutné cookies povolené.
 * Analytika / marketing / preferencie sú OPT-IN (false).
 *
 * @type {ConsentState}
 */
export const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: null,
  version: POLICY_VERSION,
};

/**
 * Načíta súhlas z localStorage.
 * Vráti null ak neexistuje alebo je zastaraný (iná verzia politiky).
 *
 * @returns {ConsentState | null}
 */
export function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    // Invalidácia pri zmene politiky – GDPR vyžaduje nový súhlas
    if (parsed.version !== POLICY_VERSION) return null;

    // Nevyhnutné sú vždy true, zabezpečíme to aj pri čítaní
    return { ...parsed, necessary: true };
  } catch {
    return null;
  }
}

/**
 * Uloží súhlas do localStorage, pridá timestamp a verziu.
 *
 * @param {Partial<ConsentState>} choices
 * @returns {ConsentState}
 */
export function saveConsent(choices) {
  const consent = {
    necessary: true, // vždy povolené, nemenné
    analytics: choices.analytics ?? false,
    marketing: choices.marketing ?? false,
    preferences: choices.preferences ?? false,
    timestamp: new Date().toISOString(),
    version: POLICY_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

/**
 * Vymaže súhlas – používateľ bude pri ďalšej návšteve opäť vidieť banner.
 */
export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
}
